import { useRef, useState, type MutableRefObject } from "react";

/**
 * El último valor **aplicado** de un filtro del side panel, serializado como viaja a la API.
 *
 * Los drawers de filtros se montan con `destroyOnClose`: cerrado el drawer, sus `Form.Item` no
 * existen, `form.submit()` no los incluye y `onSearch` los recibe `undefined`. Como el listado
 * reconstruye los filtros enteros en cada submit (`setFilters(..., "replace")`), cualquier submit
 * —el inicial con la URL, o uno del buscador principal— pisaría lo aplicado desde el drawer. Es la
 * clase de bug de QA #273.8 (`participacionDeUrl` en Contactos).
 *
 * Esto lo sostiene: arranca con lo que vino por URL, toma el valor del drawer cada vez que éste
 * manda, y se suelta cuando se cierra el chip.
 */
export interface AppliedFilterState {
  ref: MutableRefObject<string | null>;
  set: (value: string | null) => void;
}

/** Lee `filters[i][field]=<field>` + `filters[i][value]=<valor>` de la URL de refine. */
export function readFilterValueFromUrl(
  field: string,
  search: string = typeof window !== "undefined" ? window.location.search : "",
): string | null {
  const qs = new URLSearchParams(search);
  for (const [k, v] of qs.entries()) {
    const m = k.match(/^filters\[(\d+)\]\[field\]$/);
    if (!m || v !== field) continue;
    const raw = qs.get(`filters[${m[1]}][value]`);
    if (raw) return raw;
  }
  return null;
}

export function useAppliedFilterValue(field: string): AppliedFilterState {
  const [value, set] = useState<string | null>(() =>
    readFilterValueFromUrl(field),
  );
  const ref = useRef(value);
  ref.current = value;
  return { ref, set };
}

/**
 * Qué mandar por `field` en este submit.
 *
 * - Si `existingFilters` ya lo trae (FilterTags sacó **otro** chip): no se duplica → `skip`.
 * - Si el drawer mandó valor (`formValue !== undefined`): se serializa y se recuerda.
 * - Si el pedido viene de FilterTags sin el campo: se cerró el chip → se suelta.
 * - Si no (drawer cerrado): se sostiene el último aplicado.
 */
export function resolveAppliedFilter<T>(opts: {
  field: string;
  formValue: T[] | undefined;
  serialize: (value: T[]) => string | undefined;
  existingFilters?: Array<{ field: string }>;
  applied: AppliedFilterState;
}): { skip: true } | { skip: false; value: string | undefined } {
  const { field, formValue, serialize, existingFilters, applied } = opts;
  if (existingFilters?.some((f) => f.field === field)) return { skip: true };
  if (formValue !== undefined) {
    const value = serialize(formValue ?? []);
    applied.set(value ?? null);
    return { skip: false, value };
  }
  if (existingFilters) {
    applied.set(null);
    return { skip: false, value: undefined };
  }
  return { skip: false, value: applied.ref.current ?? undefined };
}
